import React, { useEffect, useMemo, useState } from 'react';
import { Button, Confirmation } from 'components/common';
import ReactDOM from 'react-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { MdOutlineClose, MdOutlineSave, MdAdd } from 'react-icons/md';
import PropTypes from 'prop-types';
import 'react-datepicker/dist/react-datepicker.css';
import { Link } from 'react-router-dom';
import { updateCsdForecast, updateCsdMonthlies, getBuildingStatuses, recalculateCsdForecast } from 'redux/crm/releaseForecastSlice';
import CsdMonthlyRow from './CsdMonthlyRow';
import {
  buildFormValues,
  emptySplitRow,
  getForecastWindow,
  getMonthOptions,
  monthLabelFor,
} from './csdForecastFormHelpers';

const MAX_MONTHS_PER_BUILDING = 3;

const TABLE_HEADERS = [
  { label: 'Building', className: 'tw-text-left tw-border-r', style: { minWidth: 120 } },
  { label: 'Phase', className: 'tw-text-center tw-border-r', style: { minWidth: 90 } },
  { label: 'Expected Date of Release', className: 'tw-text-center tw-text-blue-700', style: { minWidth: 130 } },
  { label: 'Month', className: 'tw-text-center tw-border-r tw-text-blue-700', style: { width: 90 } },
  { label: 'B/ups', className: 'tw-text-center' },
  { label: 'C/F', className: 'tw-text-center' },
  { label: 'SH', className: 'tw-text-center tw-border-r' },
  { label: 'Phase Status', className: 'tw-text-center', style: { minWidth: 150 } },
  { label: 'Source Doc', className: 'tw-text-center', style: { minWidth: 120 } },
  { label: 'Actions', className: 'tw-text-center', style: { width: 96 } },
];

const InfoItem = ({ label, children }) => (
  <div>
    <div className="tw-text-gray-500 tw-text-xs tw-mb-0.5">{label}</div>
    <div className="tw-font-semibold tw-text-sm">{children}</div>
  </div>
);
InfoItem.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const MonthTotalCard = ({ total }) => (
  <div className="tw-bg-blue-50 tw-rounded tw-px-4 tw-py-2.5 tw-text-xs tw-border tw-border-blue-100">
    <div className="tw-font-bold tw-text-blue-700 tw-mb-1.5">{total.label}</div>
    {[['B/ups', total.built_up], ['C/F', total.cold_formed], ['SH', total.sheeting]].map(([label, value]) => (
      <div key={label} className="tw-text-gray-600 tw-leading-5">
        {label}: <span className="tw-font-semibold tw-text-gray-800">{value.toFixed(2)}</span>
      </div>
    ))}
  </div>
);
MonthTotalCard.propTypes = { total: PropTypes.object.isRequired };

// ── component ─────────────────────────────────────────────────────────────────

const CsdForecastEditForm = ({ show, onHide, record, onSaved }) => {
  const dispatch = useDispatch();
  const [buildingStatuses, setBuildingStatuses] = useState([]);

  // ── localRecord: the form's source of truth ─────────────────────────────────
  // Mirrors the `record` prop; Recalculate patches its row in the form directly
  // rather than replacing this, so unsaved edits survive.
  const [localRecord, setLocalRecord] = useState(record);

  // Sync whenever the parent passes a genuinely new record (e.g. after Save)
  useEffect(() => { setLocalRecord(record); }, [record]);

  useEffect(() => {
    dispatch(getBuildingStatuses()).then((action) => {
      const data = action.payload?.data || action.payload || [];
      setBuildingStatuses(Array.isArray(data) ? data : []);
    });
  }, [dispatch]);

  const period = localRecord?.period;
  const monthOptions = useMemo(() => getMonthOptions(period), [period]);
  const forecastWindow = useMemo(() => getForecastWindow(period), [period]);

  // Flat structure: one entry per (building × month)
  const defaultValues = useMemo(() => buildFormValues(localRecord), [localRecord]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { isSubmitting },
  } = useForm({ defaultValues });

  // keyName "_key" so the generated React key does not overwrite the row's DB id,
  // which the server needs to move a row when its date changes month.
  const { fields, append, remove: removeRow } = useFieldArray({ control, name: 'monthlies', keyName: '_key' });

  // Reset the form whenever localRecord changes (initial load or parent sync)
  useEffect(() => { reset(defaultValues); }, [localRecord, reset, defaultValues]);

  useEffect(() => {
    document.body.style.overflow = show ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [show]);

  const watchedMonthlies = watch('monthlies');

  // Per-month totals for summary footer. Rows without a date have no month yet.
  const monthTotals = useMemo(() => {
    const acc = {};
    monthOptions.forEach((m) => {
      acc[m.index] = { label: m.label, built_up: 0, cold_formed: 0, sheeting: 0 };
    });
    (watchedMonthlies || []).forEach((m) => {
      const t = acc[m.month_index];
      if (t) {
        t.built_up    += parseFloat(m.built_up_weight)    || 0;
        t.cold_formed += parseFloat(m.cold_formed_weight) || 0;
        t.sheeting    += parseFloat(m.sheeting_weight)    || 0;
      }
    });
    return monthOptions.map((m) => acc[m.index]);
  }, [watchedMonthlies, monthOptions]);

  const rowCountFor = (buildingId) => fields.filter((f) => f.building_id === buildingId).length;

  const handleDeleteRow = (index) => {
    if (rowCountFor(fields[index].building_id) <= 1) {
      toast.error('Cannot remove the last month entry for a building');
      return;
    }
    removeRow(index);
  };

  const handleAddSplit = (index) => {
    const row = fields[index];
    if (rowCountFor(row.building_id) >= MAX_MONTHS_PER_BUILDING) {
      toast.info('All months already assigned for this building');
      return;
    }
    append(emptySplitRow(row));
  };

  const [calculatingIdx, setCalculatingIdx] = useState(null);
  const [confirmCalc, setConfirmCalc]       = useState({ open: false, index: null });

  // Step 1 — open confirmation dialog
  const handleCalculateRow = (index) => {
    const row = fields[index];
    if (!record?.id || !row.building_id) return;
    setConfirmCalc({ open: true, index });
  };

  // Step 2 — user confirmed: run the API call, then patch ONLY that row's weights
  // and source document. Resetting the whole form would wipe every release date
  // typed but not yet saved. Does NOT call onSaved() — the parent list is only
  // synced on explicit Save, so the form stays open for further editing.
  const handleConfirmCalculate = async () => {
    const { index } = confirmCalc;
    setConfirmCalc({ open: false, index: null });

    const row = getValues(`monthlies.${index}`);
    setCalculatingIdx(index);
    try {
      const result = await dispatch(recalculateCsdForecast({
        csdId:      localRecord.id,
        monthIndex: row.saved_month_index,
        buildingId: row.building_id,
      })).unwrap();

      const updated = result?.data?.attributes || result?.attributes || result;
      const month = (updated?.buildings || [])
        .find((b) => b.building_id === row.building_id)?.months
        ?.find((m) => m.month_index === row.saved_month_index);
      if (month) {
        ['built_up_weight', 'cold_formed_weight', 'sheeting_weight'].forEach((name) => {
          setValue(`monthlies.${index}.${name}`, month[name] || '', { shouldDirty: true });
        });
        setValue(`monthlies.${index}.source_document`, month.source_document || null);
      }

      toast.success('Recalculated successfully — weights updated.');
    } catch {
      toast.error('Recalculate failed. Please try again.');
    } finally {
      setCalculatingIdx(null);
    }
  };

  const onSubmit = async (data) => {
    if (!record?.id) {
      toast.error('Record ID is missing. Please reload the form.');
      return;
    }

    // The project-level release date is derived server-side from the rows.
    const csdParams = {
      percent_change_of_release:
        data.percent_change_of_release !== '' ? Number(data.percent_change_of_release) : null,
      remark: data.remark,
    };

    const monthlies = data.monthlies.map((m) => ({
      id:                    m.id || null,
      building_id:           m.building_id,
      expected_release_date: m.expected_release_date,
      built_up_weight:       parseFloat(m.built_up_weight)    || 0,
      cold_formed_weight:    parseFloat(m.cold_formed_weight) || 0,
      sheeting_weight:       parseFloat(m.sheeting_weight)    || 0,
      project_status_name:   m.project_status_name || null,
      phase:                 m.phase || null,
      remark:                m.remark,
    }));

    try {
      await dispatch(updateCsdForecast({ csdId: record.id, data: csdParams })).unwrap();
      await dispatch(updateCsdMonthlies({ csdId: record.id, monthlies })).unwrap();
      toast.success('CSD Forecast updated successfully');
      onSaved?.();
      onHide();
    } catch (err) {
      toast.error(err?.error || err?.message || 'Failed to save CSD Forecast');
    }
  };

  const project  = record?.project || {};
  const isLocked = !!record?.sent_at;

  if (!show) return null;

  const confirmRow = confirmCalc.open ? getValues(`monthlies.${confirmCalc.index}`) : null;

  return ReactDOM.createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-flex tw-justify-end"
      style={{ zIndex: 99999 }}
    >
      {/* Backdrop */}
      <div className="tw-absolute tw-inset-0 tw-bg-black tw-bg-opacity-50" />

      {/* Panel */}
      <div
        className="tw-relative tw-bg-white tw-h-full tw-shadow-2xl tw-flex tw-flex-col"
        style={{ width: '75%', minWidth: 600, maxWidth: 1180 }}
      >
        {/* Header */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-100 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
          <div className="tw-flex tw-items-center tw-gap-3">
            <h5 className="tw-font-semibold tw-text-base tw-mb-0">Edit CSD Release Forecast</h5>
            {isLocked && (
              <span className="tw-inline-block tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-bg-green-100 tw-text-green-800 tw-rounded-sm">
                Released
              </span>
            )}
          </div>
          <Button onClick={onHide} variant="secondary" aria-label="Close">✕</Button>
        </div>

        {/* Scrollable Body */}
        <div className="tw-flex-1 tw-overflow-y-auto tw-p-6">

          {/* Section A — Project Info */}
          <div className="tw-bg-gray-100 tw-rounded tw-p-4 tw-mb-6">
            <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-mb-3">
              <InfoItem label="Project">
                <Link to={`/crm/projects/${project.id}`} target="_blank" className="tw-text-blue-500 tw-hover:tw-no-underline">
                  {project.name || '—'}
                </Link>
              </InfoItem>
              <InfoItem label="Q/J Number">{project.j_number || project.q_number || '—'}</InfoItem>
              <InfoItem label="Period">{record?.period || '—'}</InfoItem>
              <InfoItem label="Customer">
                <Link to={`/crm/customers/${project.customer_id}`} target="_blank" className="tw-text-blue-500 tw-hover:tw-no-underline">
                  {project.customer_name || '—'}
                </Link>
              </InfoItem>
              <InfoItem label="Sales Incharge">{project.sales_incharge || '—'}</InfoItem>
            </div>
          </div>

          <form id="csdForecastForm" onSubmit={handleSubmit(onSubmit)}>

            {/* Section B — Forecast Parameters */}
            {/* One row: a narrow % field beside a full-width remark (stacks on narrow screens). */}
            <div className="tw-mb-6">
              <h6 className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-3">
                Forecast Parameters
              </h6>
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-[180px_minmax(0,1fr)] tw-gap-4 tw-items-start">
                <div>
                  <label htmlFor="csd-forecast-percent" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">
                    % Chance of Release
                  </label>
                  <Controller
                    control={control}
                    name="percent_change_of_release"
                    rules={{
                      min: { value: 0, message: 'Min 0' },
                      max: { value: 100, message: 'Max 100' },
                      required: 'Percent Chance of Release is required',
                    }}
                    render={({ field: f, fieldState: { error } }) => (
                      <>
                        <input
                          {...f}
                          id="csd-forecast-percent"
                          type="number" min={0} max={100} step={1} placeholder="0–100"
                          disabled={isLocked}
                          className={`tw-block tw-w-full tw-h-[38px] tw-px-3 tw-py-2 tw-border tw-rounded-md tw-text-sm disabled:tw-bg-gray-100 ${
                            error ? 'tw-border-red-500' : 'tw-border-gray-300'
                          }`}
                        />
                        {error && <p className="tw-text-red-600 tw-text-xs tw-mt-1">{error.message}</p>}
                      </>
                    )}
                  />
                </div>

                <div>
                  <label htmlFor="csd-forecast-remark" className="tw-block tw-text-sm tw-font-medium tw-text-gray-700 tw-mb-2">Remark</label>
                  {/* Starts one line tall to line up with the % field; drag to grow.
                      font-family inherit: without Tailwind preflight a textarea falls back to monospace. */}
                  <textarea
                    id="csd-forecast-remark"
                    rows={1}
                    placeholder="Enter remark…"
                    disabled={isLocked}
                    className="tw-block tw-w-full tw-min-h-[38px] tw-px-3 tw-py-2 tw-border tw-border-gray-300 tw-rounded-md tw-text-sm tw-leading-5 tw-resize-y disabled:tw-bg-gray-100"
                    style={{ fontFamily: 'inherit' }}
                    {...register('remark')}
                  />
                </div>
              </div>
            </div>

            {/* Section C — Building Monthly Weights */}
            <div>
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-mb-4">
                <h6 className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-wide tw-mb-0">
                  Building Monthly Weights (MT)
                </h6>
                {!isLocked && (
                  <span className="tw-text-xs tw-text-gray-400">
                    Pick the Expected Date of Release — the Month follows it. Click <MdAdd className="tw-inline" size={12} /> to split a building across months
                  </span>
                )}
              </div>

              {fields.length === 0 ? (
                <p className="tw-text-gray-500 tw-text-sm">No buildings found for this project.</p>
              ) : (
                <>
                  <div className="tw-overflow-x-auto tw-border tw-border-gray-200 tw-rounded-sm">
                    <table className="tw-w-full tw-text-xs tw-border-collapse">
                      <thead>
                        <tr className="tw-bg-gray-200">
                          {TABLE_HEADERS.map((h) => (
                            <th
                              key={h.label}
                              className={`tw-px-2 tw-py-2 tw-border-b tw-border-gray-300 tw-font-semibold ${h.className}`}
                              style={h.style}
                            >
                              {h.label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((field, idx) => {
                          const row = watchedMonthlies?.[idx];
                          const buildingRowCount = rowCountFor(field.building_id);
                          const monthChanged = row?.month_index !== row?.saved_month_index;

                          return (
                            <CsdMonthlyRow
                              key={field._key}
                              index={idx}
                              field={field}
                              row={row}
                              period={period}
                              forecastWindow={forecastWindow}
                              control={control}
                              setValue={setValue}
                              getValues={getValues}
                              buildingStatuses={buildingStatuses}
                              isLocked={isLocked}
                              isFirstRow={fields.findIndex((f) => f.building_id === field.building_id) === idx}
                              canSplit={!isLocked && buildingRowCount < MAX_MONTHS_PER_BUILDING}
                              canDelete={!isLocked && buildingRowCount > 1}
                              canRecalculate={!!row?.id && !monthChanged}
                              calculating={calculatingIdx === idx}
                              onSplit={handleAddSplit}
                              onCalculate={handleCalculateRow}
                              onDelete={handleDeleteRow}
                            />
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Month totals */}
                  <div className="tw-mt-4 tw-flex tw-gap-3 tw-flex-wrap tw-items-center tw-justify-center">
                    {monthTotals.map((t) => <MonthTotalCard key={t.label} total={t} />)}
                  </div>
                </>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="tw-px-6 tw-py-4 tw-bg-gray-100 tw-border-t tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-flex-shrink-0">
          {!isLocked ? (
            <>
              <Button variant="primary" type="submit" form="csdForecastForm" disabled={isSubmitting}>
                <MdOutlineSave />
                {isSubmitting ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="danger" type="button" onClick={onHide} disabled={isSubmitting}>
                <MdOutlineClose />
                Cancel
              </Button>
            </>
          ) : (
            <Button variant="secondary" type="button" onClick={onHide}>
              <MdOutlineClose />
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Recalculate confirmation */}
      {confirmRow && (
        <Confirmation
          show
          type="warning"
          title="Recalculate weights?"
          message={
            <span>
              Recalculate <strong>{confirmRow.building_name || `Building ${confirmRow.building_id}`}</strong> for{' '}
              <strong>{monthLabelFor(period, confirmRow.saved_month_index) || `Month ${confirmRow.saved_month_index}`}</strong> from the source document?
              <br />
              <span className="tw-text-slate-500 tw-text-xs tw-mt-1 tw-block">
                Current weights will be overwritten with values from the linked source document.
              </span>
            </span>
          }
          confirmText="Yes, recalculate"
          cancelText="Cancel"
          onConfirm={handleConfirmCalculate}
          onCancel={() => setConfirmCalc({ open: false, index: null })}
        />
      )}
    </div>,
    document.body
  );
};

CsdForecastEditForm.propTypes = {
  show:    PropTypes.bool.isRequired,
  onHide:  PropTypes.func.isRequired,
  record:  PropTypes.object,
  onSaved: PropTypes.func,
};

CsdForecastEditForm.defaultProps = {
  record:  null,
  onSaved: null,
};

export default CsdForecastEditForm;
